import React, { useMemo } from 'react';
import { ArrowLeft, Info, TrendingUp, Calendar, Zap } from 'lucide-react';
import { useRateDetail } from '../../../../hooks';
import { RateDetail } from '../../../../types';
import { useParams, useNavigate } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { usePageViewTimer } from '@shared/hooks/usePageViewTimer';
import { useCurrency } from '@shared/selectors/globalSelectors';

// Configuration object for all rate types
const RATE_CONFIG = {
  FIXED: {
    label: 'Fixed',
    color: 'bg-blue-100 text-blue-800',
    icon: TrendingUp,
    description: 'This fixed rate tariff applies a consistent unit rate per unit regardless of consumption volume or time of use.',
    benefits: [
      'Predictable billing amounts',
      'Simple rate structure',
      'Easy to understand and budget',
    ],
    tips: [
      'Monitor consumption patterns for potential savings',
      'Consider switching during low-usage periods',
    ],
    tags: [
      { label: 'Predictable costs', color: 'bg-blue-50 text-blue-700' },
      { label: 'Simple billing', color: 'bg-blue-50 text-blue-700' },
      { label: 'Budget-friendly', color: 'bg-blue-50 text-blue-700' },
    ],
  },
  FLAT: {
    label: 'Flat',
    color: 'bg-green-100 text-green-800',
    icon: TrendingUp,
    description: 'This flat rate tariff applies a uniform rate across all consumption levels.',
    benefits: [
      'Uniform pricing across all usage',
      'No consumption tier penalties',
      'Straightforward billing',
    ],
    tips: [
      'Suitable for consistent usage patterns',
      'Good for medium consumption levels',
    ],
    tags: [
      { label: 'Uniform pricing', color: 'bg-green-50 text-green-700' },
      { label: 'No tier penalties', color: 'bg-blue-50 text-blue-700' },
      { label: 'Consistent rates', color: ' text-gray-700' },
    ],
  },
  SLAB: {
    label: 'Slab',
    color: 'bg-blue-100 text-blue-800',
    icon: TrendingUp,
    description: 'This slab rate tariff applies different unit rates based on consumption levels.',
    benefits: [
      'Rewards lower consumption with better rates',
      'Encourages conservation and efficiency',
      'Good for variable consumption needs',
    ],
    tips: [
      'Track consumption to stay in lower slabs',
      'Install efficient appliances to reduce usage',
    ],
    tags: [
      { label: 'Rewards efficient consumption', color: 'bg-green-50 text-green-700' },
      { label: 'Rates change with tiers', color: 'bg-yellow-50 text-yellow-700' },
      { label: 'Encouraging conservation', color: 'bg-blue-50 text-blue-700' },
    ],
  },
  'TIME OF USE': {
    label: 'Time-of-Use',
    color: 'bg-indigo-100 text-indigo-800',
    icon: Calendar,
    description: 'This time-of-use tariff applies different rates based on the time of day.',
    benefits: [
      'Potential savings by shifting usage to off-peak',
      'Reduces grid strain during peak hours',
      'Good for flexible usage schedules',
    ],
    tips: [
      'Schedule high-consumption activities during off-peak',
      'Use timers for appliances to run during cheaper hours',
    ],
    tags: [
      { label: 'Save during low-rate periods', color: 'bg-blue-50 text-blue-700' },
      { label: 'Rate changes by time', color: 'bg-blue-50 text-blue-700' },
      { label: 'Flexible consumers', color: 'bg-green-50 text-green-700' },
    ],
  },
  SEASONAL: {
    label: 'Seasonal',
    color: 'bg-orange-100 text-orange-800',
    icon: Calendar,
    description: 'This seasonal tariff applies different rates based on the time of year.',
    benefits: [
      'Reflects actual seasonal energy costs',
      'Allows planning for seasonal variations',
      'Good for predictable annual usage patterns',
    ],
    tips: [
      'Plan high-consumption activities during favorable seasons',
      'Adjust usage patterns with seasonal changes',
    ],
    tags: [
      { label: 'Reflects seasonal costs', color: 'bg-orange-50 text-orange-700' },
      { label: 'Rate changes by season', color: 'bg-blue-50 text-blue-700' },
      { label: 'Predictable usage patterns', color: 'bg-green-50 text-green-700' },
    ],
  },
};

// Utility functions
const formatDate = (dateStr: string): string => {
  try {
    // Handle formats like "09 Jul 2025" or standard date formats
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      // If standard parsing fails, try custom parsing for "DD MMM YYYY" format
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const [day, month, year] = dateStr.split(' ');
      const monthIndex = months.indexOf(month);
      if (monthIndex !== -1) {
        return new Date(parseInt(year), monthIndex, parseInt(day)).toLocaleDateString();
      }
    }
    return date.toLocaleDateString();
  } catch {
    return dateStr; // Return original if parsing fails
  }
};

const formatTime = (time: number | string): string => {
  if (typeof time === "string") {
    // If it's already a time string, return as is
    if (time.includes(':')) return time;
    // If it's a date string, extract time or return as date
    return formatDate(time);
  }
  // Convert decimal hours to HH:MM format
  const hours = Math.floor(time);
  const minutes = Math.round((time - hours) * 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Generic Rate Display Component
const RateDisplay: React.FC<{ 
  rateDetail: RateDetail;
  config: typeof RATE_CONFIG[keyof typeof RATE_CONFIG];
}> = ({ rateDetail, config }) => {
  const rateItems = rateDetail?.rate || [];

  const currencyDisplay = useCurrency();

  const renderContent = () => {
    const effectiveType = rateDetail.rateType === 0 ? 'FIXED' : rateDetail.basisType?.toUpperCase();

    switch (effectiveType) {
      case 'FIXED':
        const fixedRate = rateItems[0]?.rate || 0;
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="card-heading-text font-semibold text-blue-900 mb-1">Base Unit Rate</h3>
              <div className="text-2xl font-bold text-blue-700">
                {currencyDisplay} {fixedRate.toFixed(4)}
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              {[100, 200, 500].map((units, index) => (
                <div key={index} className="bg-white border rounded-lg p-3 text-center">
                  <div className="text-sm font-medium text-gray-600">{units} Units</div>
                  <div className="text-lg font-bold text-blue-600">
                    {currencyDisplay} {(units * fixedRate).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'FLAT':
        const flatRate = rateItems[0]?.rate || 0;
        return (
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-green-900 mb-1">Flat Unit Rate</h3>
            <div className="text-2xl font-bold text-green-700">
              {currencyDisplay} {flatRate.toFixed(4)}
            </div>
            <p className="text-green-700 mt-2 text-sm">
              This rate applies to all consumption regardless of usage amount.
            </p>
          </div>
        );

      case 'SLAB':
        return (
          <div className="space-y-3">
            <div className="overflow-hidden bg-white rounded-lg border">
              <div className="grid grid-cols-2  p-3 text-sm font-medium text-gray-700">
                <div>CONSUMPTION RANGE</div>
                <div>UNIT RATE</div>
              </div>
              
              {(Array.isArray(rateItems) ? rateItems : []).map((item, index) => (
                <div key={index} className="grid grid-cols-2 p-3 border-t border-gray-100 text-sm">
                  <div className="text-gray-600">
                    {index === (Array.isArray(rateItems) ? rateItems.length : 0) - 1 
                      ? `${item.startTime || 0}+ units`
                      : `${item.startTime || 0} - ${item.endTime || 0} units`
                    }
                  </div>
                  <div className="font-semibold text-blue-600">
                    {currencyDisplay} {item.rate.toFixed(4)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'TIME OF USE':
        return (
          <div className="space-y-3">
            <div className="overflow-hidden bg-white rounded-lg border">
              <div className="grid grid-cols-2  p-3 text-sm font-medium text-gray-700">
                <div>TIME PERIOD</div>
                <div>UNIT RATE</div>
              </div>

              {(Array.isArray(rateItems) ? rateItems : []).map((item, index) => (
                <div key={index} className="grid grid-cols-2 p-3 border-t border-gray-100 text-sm">
                  <div className="text-gray-600">
                    <div className="font-medium text-gray-900">
                      {item.startTime} - {item.endTime}
                    </div>
                  </div>
                  <div className="font-semibold text-indigo-600">
                    {currencyDisplay} {item.rate.toFixed(4)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'SEASONAL':
        return (
          <div className="space-y-3">
            <div className="overflow-hidden bg-white rounded-lg border">
              <div className="grid grid-cols-3  p-3 text-sm font-medium text-gray-700">
                <div>SEASON</div>
                <div>PERIOD</div>
                <div>UNIT RATE</div>
              </div>
              
              {(Array.isArray(rateItems) ? rateItems : []).map((item, index) => (
                <div key={index} className="grid grid-cols-3 p-3 border-t border-gray-100 text-sm">
                  <div className="font-medium text-gray-900">
                    {item.seasonName || `Season ${index + 1}`}
                  </div>
                  <div className="text-gray-600">
                    {item.startTime} - {item.endTime}
                  </div>
                  <div className="font-semibold text-orange-600">
                    {currencyDisplay} {item.rate.toFixed(4)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div className="text-gray-500 p-4 bg-yellow-50 rounded-lg">
            <p>Unknown rate type: {effectiveType}</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {renderContent()}
      
      {/* Description */}
      <div className="p-3  rounded-lg">
        <div className="flex items-start">
          <Info className="w-4 h-4 text-gray-500 mt-0.5 mr-2 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              About {config.label.toLowerCase()} rates
            </h4>
            <p className="text-sm text-gray-600">{config.description}</p>
          </div>
        </div>
      </div>

      {/* Tags */}
      <div className="grid grid-cols-3 gap-2">
        {config.tags.map((tag, index) => (
          <div key={index} className="text-center">
            <div className={`p-2 rounded-lg ${tag.color}`}>
              <config.icon className="w-4 h-4 mx-auto mb-1" />
              <div className="text-xs font-medium">{tag.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Info Panel Component
const InfoPanel: React.FC<{ rateDetail: RateDetail }> = ({ rateDetail }) => {
  const infoItems = [
    { label: 'Name', value: rateDetail.rateName },
    { label: 'Utility', value: rateDetail.utilityService },
    { label: 'Rate Type', value: rateDetail.rateTypeDisplay },
    { label: 'From', value: rateDetail.effectiveDate },
    { label: 'To', value: rateDetail.expiryDate ? rateDetail.expiryDate : 'Ongoing' },
    { label: 'Created', value: rateDetail.createdDate },
    { label: 'Version', value: rateDetail.version },
    { label: 'Status', value: rateDetail.status },
  ];

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center mb-3">
        <Info className="w-4 h-4 text-gray-500 mr-2" />
        <h2 className="text-sm font-medium text-gray-900">Tariff Information</h2>
      </div>
      
      <div className="space-y-2">
        {infoItems.map((item, index) => (
          <div key={index} className="flex justify-between text-xs">
            <span className="text-gray-500">{item.label}</span>
            <span className="font-medium text-gray-900 text-right max-w-[60%]">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Benefits Panel Component
const BenefitsPanel: React.FC<{ config: typeof RATE_CONFIG[keyof typeof RATE_CONFIG] }> = ({ config }) => (
  <div className="bg-white rounded-lg border p-4">
    <div className="flex items-center mb-3">
      <Zap className="w-4 h-4 text-gray-500 mr-2" />
      <h2 className="text-sm font-medium text-gray-900">Analysis & Benefits</h2>
    </div>

    <div className="space-y-4">
      <div>
        <h3 className="text-xs font-medium text-gray-900 mb-2">Key Benefits</h3>
        <ul className="space-y-1">
          {config.benefits.map((benefit, index) => (
            <li key={index} className="flex items-start">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></div>
              <span className="text-xs text-gray-600">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-xs font-medium text-gray-900 mb-2">Optimization Tips</h3>
        <ul className="space-y-1">
          {config.tips.map((tip, index) => (
            <li key={index} className="flex items-start">
              <div className="w-3 h-3 bg-blue-100 rounded flex items-center justify-center mt-1 mr-2 flex-shrink-0">
                <div className="w-1 h-1 bg-blue-500 rounded-full"></div>
              </div>
              <span className="text-xs text-gray-600">{tip}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

// Change History Table Columns
const changeHistoryColumns: TableColumn[] = [
  {
    key: "version",
    header: "Version",
    width: "w-20",
    render: (value) => <span className="text-xs font-medium text-gray-900">{value}</span>,
  },
  {
    key: "lastModifiedDate",
    header: "Date Modified",
    width: "w-28",
    render: (value: string) => (
      <span className="text-xs text-gray-600">{formatDate(value)}</span>
    ),
  },
  {
    key: "lastModifiedUserRemoteName",
    header: "Updated By",
    width: "w-32",
    render: (value) => <span className="text-xs text-gray-700">{value || 'System'}</span>,
  },
  {
    key: "changes",
    header: "Changes",
    width: "w-auto",
    type: "custom",
    render: (changes: any) => {
      if (!changes || Object.keys(changes).length === 0) {
        return <span className="text-gray-500 text-xs">No changes recorded</span>;
      }
      
      return (
        <div className="space-y-1">
          {Object.entries(changes).map(([field, change]: [string, any]) => (
            <div key={field} className="text-xs">
              <span className="font-medium text-gray-700 capitalize">
                {field.replace(/([A-Z])/g, ' $1').trim()}:
              </span>
              <div className="ml-2 mt-0.5 flex items-center space-x-1">
                <span className="text-red-600 line-through text-xs">
                  {typeof change.old === 'string' ? change.old : JSON.stringify(change.old)}
                </span>
                <span className="text-gray-400 text-xs">→</span>
                <span className="text-blue-600 text-xs">
                  {typeof change.new === 'string' ? change.new : JSON.stringify(change.new)}
                </span>
              </div>
            </div>
          ))}
        </div>
      );
    },
  },
];

// Main Component
const RateDetailComponent: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const rateId = Number(id);
  usePageViewTimer({pageName: `Tariff Detail Viewed - ${id}`})
  const { data: rateDetail, isLoading, error } = useRateDetail(rateId);
  console.log('rateDetail hook data',JSON.stringify(rateDetail,null,2))

  // Get configuration based on rate type and basis type
  const config = useMemo(() => {
    if (!rateDetail) return null;
    
    const effectiveType = rateDetail.rateType === 0 
      ? 'FIXED' 
      : rateDetail.basisType?.toUpperCase() || 'FLAT';
    
    return RATE_CONFIG[effectiveType as keyof typeof RATE_CONFIG] || RATE_CONFIG.FIXED;
  }, [rateDetail]);

   if (!isLoading && (error || !rateDetail)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️ Error loading rate details</div>
          <p className="text-gray-600">{error?.message || 'Rate not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return !isLoading && (
     <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900 text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </button>
            <h1 className="text-lg font-medium text-gray-900">{rateDetail.rateName}</h1>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              rateDetail.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
            }`}>
              {rateDetail.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="text-xs text-gray-500">
            Updated: {rateDetail.lastModifiedDate}
          </div>
        </div>
      </div>

      <div className="pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Info Panel */}
          <div className="lg:col-span-1">
            <InfoPanel rateDetail={rateDetail} />
          </div>

          {/* Rate Configuration */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <TrendingUp className="w-4 h-4 text-gray-500 mr-2" />
                  <h2 className="text-sm font-medium text-gray-900">Rate Configuration</h2>
                </div>
                {config && (
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                    {config.label}
                  </span>
                )}
              </div>

              {config && <RateDisplay rateDetail={rateDetail} config={config} />}
            </div>
          </div>

          {/* Benefits Panel */}
          <div className="lg:col-span-1">
            {config && <BenefitsPanel config={config} />}
          </div>
        </div>

        {/* Change History */}
        {rateDetail.transactionData && rateDetail.transactionData.length > 0 && (
          <div className="mt-4">
            <div className="bg-white rounded-lg border p-4">
              <div className="flex items-center mb-4">
                <Info className="w-4 h-4 text-gray-500 mr-2" />
                <h2 className="text-sm font-medium text-gray-900">Change History</h2>
              </div>

              <GenericTable
                data={rateDetail.transactionData}
                columns={changeHistoryColumns}
                loading={false}
                showFilters={false}
                rowKey={'id'}
                emptyMessage="No change history found"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RateDetailComponent;